import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Copy, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const GmailSetup = () => {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<"idle" | "connecting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [tokens, setTokens] = useState<{ FEEDOPTIMIZER_EMAIL: string; FEEDOPTIMIZER_GMAIL_REFRESH_TOKEN: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    // Check if we're coming back from OAuth
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const setupComplete = params.get('setupComplete');

    if (success === 'true' && setupComplete === 'true') {
      const email = params.get('email');
      const refreshToken = params.get('refreshToken');

      if (email && refreshToken) {
        setStatus('success');
        setMessage(t('gmailSetup.oauthSuccess'));
        setTokens({
          FEEDOPTIMIZER_EMAIL: email,
          FEEDOPTIMIZER_GMAIL_REFRESH_TOKEN: refreshToken,
        });
      }
    }
  }, []);

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleConnectGmail = () => {
    setStatus("connecting");

    // Gmail OAuth scopes for reading and modifying emails
    const scopes = [
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/gmail.modify',
      'https://www.googleapis.com/auth/userinfo.email',
      'openid',
    ].join(' ');

    const clientId = "983718304542-24tiguofg85l37d0n1l247vsthc6821i.apps.googleusercontent.com";
    const redirectUri = `${window.location.origin}/auth/google/callback`;

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', scopes);
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('prompt', 'consent');
    authUrl.searchParams.append('state', 'gmail_setup'); // Special state to identify this flow

    // Store that we're in Gmail setup mode
    sessionStorage.setItem('gmail_setup_mode', 'true');

    window.location.href = authUrl.toString();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('gmailSetup.title')}</CardTitle>
          <CardDescription>
            {t('gmailSetup.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('gmailSetup.description')}
            </AlertDescription>
          </Alert>

          {status === "success" && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-success" />
              <AlertDescription className="text-success-ink">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {status === "error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {message}
              </AlertDescription>
            </Alert>
          )}

          {tokens && (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-success" />
                <AlertDescription>
                  {t('gmailSetup.copyInstructions')}
                </AlertDescription>
              </Alert>

              {Object.entries(tokens).map(([key, value]) => (
                <div key={key} className="space-y-2">
                  <label className="text-sm font-semibold">{key}</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={value}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(value, key)}
                    >
                      {copiedField === key ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}

              <Alert>
                <AlertDescription className="text-xs">
                  <strong>{t('gmailSetup.nextSteps')}</strong>
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>{t('gmailSetup.nextStep1')}</li>
                    <li>{t('gmailSetup.nextStep2')}</li>
                    <li>{t('gmailSetup.nextStep3')}</li>
                    <li>{t('gmailSetup.nextStep4')}</li>
                  </ol>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="font-semibold">{t('gmailSetup.whatItDoes')}</h3>
            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
              <li>{t('gmailSetup.feature1')}</li>
              <li>{t('gmailSetup.feature2')}</li>
              <li>{t('gmailSetup.feature3')}</li>
              <li>{t('gmailSetup.feature4')}</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">{t('gmailSetup.requiredScopes')}</h3>
            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
              <li>{t('gmailSetup.scope1')}</li>
              <li>{t('gmailSetup.scope2')}</li>
              <li>{t('gmailSetup.scope3')}</li>
            </ul>
          </div>

          <Button
            onClick={handleConnectGmail}
            disabled={status === "connecting"}
            className="w-full"
          >
            {status === "connecting" ? t('gmailSetup.connecting') : t('gmailSetup.connectButton')}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            {t('gmailSetup.signInNote')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default GmailSetup;
